import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Phone, Camera } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const config: { logo?: string; phone: string; ctaText: string; ctaLink: string; menuItems: { label: string; link: string }[] } = {
    phone: '9842489313',
    ctaText: 'BOOK US NOW',
    ctaLink: '/contact',
    menuItems: [
      { label: 'Weddings', link: '/weddings' },
      { label: 'Baby Photography', link: '/baby-photography' },
      { label: 'Gallery', link: '/gallery' },
      { label: 'Destination Weddings', link: '/destination-weddings' },
      { label: 'About Us', link: '/about' },
      { label: 'Contact', link: '/contact' },
    ],
  };

  const menuItems = Array.isArray(config.menuItems) ? config.menuItems : [];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`sticky top-0 z-50 transition-all duration-300 overflow-hidden w-full ${
        scrolled
          ? 'shadow-2xl shadow-primary-500/20'
          : 'shadow-lg shadow-primary-500/10'
      }`}
      style={{ background: 'linear-gradient(135deg, #701a75 0%, #4c1d95 40%, #1e1b4b 100%)' }}
    >
      {/* Colorful top accent bar */}
      <div className="h-1 w-full bg-gradient-to-r from-primary-400 via-rose-400 via-amber-400 via-emerald-400 to-cyan-400" />

      <div className="flex items-center h-16 md:h-20 relative overflow-hidden w-full">
        {/* Logo */}
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-6 py-1.5 md:py-4 h-full flex items-center flex-shrink-0 md:mr-8"
          style={{ background: 'rgba(255,255,255,0.08)' }}
        >
          <Link to="/" className="flex items-center gap-1 sm:gap-2 md:gap-2 lg:gap-3 w-full md:w-auto">
            {config.logo ? (
              <img src={config.logo} alt="AANAND VIDEO COVERAGE" className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto object-contain flex-shrink-0" />
            ) : (
              <motion.div
                animate={{ rotate: [0, -10, 10, 0] }}
                transition={{ duration: 1.2, ease: 'easeInOut', repeat: Infinity, repeatDelay: 3 }}
              >
                <Camera className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 lg:h-12 lg:w-12 text-primary-300 flex-shrink-0 drop-shadow-lg" />
              </motion.div>
            )}
            <span className="text-xl md:text-xl lg:text-2xl font-bold whitespace-nowrap animated-gradient-text">
              AANAND VIDEO COVERAGE
            </span>
          </Link>
        </motion.div>

        {/* Navigation Container */}
        <div className="flex items-center ml-auto md:ml-0 px-2 sm:px-4 md:px-0 min-w-0">
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4 lg:space-x-6 xl:space-x-8 flex-nowrap whitespace-nowrap">
            {menuItems.map((item: any, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={item.link || '#'}
                  className="text-sm lg:text-base text-purple-200 hover:text-white transition-colors relative group font-medium"
                >
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-400 to-rose-400 transition-all group-hover:w-full rounded-full"></span>
                </Link>
              </motion.div>
            ))}
            <a
              href={`tel:${config.phone}`}
              className="flex items-center text-sm lg:text-base text-amber-300 hover:text-amber-200 ml-2 lg:ml-3 xl:ml-4 flex-shrink-0 font-medium"
            >
              <motion.div
                animate={{ rotate: [0, -15, 15, 0] }}
                transition={{ duration: 1.5, ease: 'easeInOut', repeat: Infinity, repeatDelay: 2 }}
              >
                <Phone className="h-4 w-4 mr-1" />
              </motion.div>
              <span className="hidden lg:inline">{config.phone}</span>
              <span className="lg:hidden">Call</span>
            </a>
            <Link
              to={config.ctaLink || '/contact'}
              className="text-sm lg:text-base px-3 lg:px-4 xl:px-5 py-2 ml-2 lg:ml-3 xl:ml-4 flex-shrink-0 whitespace-nowrap rounded-xl font-bold transition-all duration-200 shadow-lg hover:shadow-rose-500/40 hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #f43f5e, #e11d48)', color: 'white' }}
            >
              {config.ctaText || 'BOOK US NOW'}
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 flex-shrink-0 text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden py-4 border-t border-white/10 overflow-hidden px-4 sm:px-6 lg:px-8"
            style={{ background: 'rgba(0,0,0,0.3)' }}
          >
            {menuItems.map((item: any, index: number) => (
              <Link
                key={index}
                to={item.link || '#'}
                className="block py-2 text-purple-200 hover:text-white font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <a
              href={`tel:${config.phone}`}
              className="block py-2 text-amber-300 hover:text-amber-200 font-medium"
            >
              <Phone className="h-4 w-4 inline mr-1" />
              {config.phone}
            </a>
            <Link
              to={config.ctaLink || '/contact'}
              className="block mt-3 text-center py-2 px-4 rounded-xl font-bold text-white shadow-lg"
              style={{ background: 'linear-gradient(135deg, #f43f5e, #e11d48)' }}
              onClick={() => setIsMenuOpen(false)}
            >
              {config.ctaText || 'BOOK US NOW'}
            </Link>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
